import { useQuery } from "@tanstack/react-query";

import { API, Auth, graphqlOperation } from "aws-amplify";

import { GraphQLQuery } from "@aws-amplify/api";

import {
  listUsers,
  getUser,
  getUserView,
  getCGPercentUsers,
  getDataUsers,
} from "@/graphql/queries";

import { fanUser, updateUser } from "@/graphql/mutations";
import { filterCategories } from "./useTags";
import useAuthStore from "@/store/useAuthStore";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { PAGE_URL } from "@/utils/constants";
import { useRouter } from "next/router";
import useMyProfileStore from "./useMyProfileStore";
import deleteDatabase from "@/utils/indexedDB/deleteDatabase";
import { useCallback } from "react";
import { getUserLite } from "@/graphql/customQueries/getUserViewInitial";
import { getFromLocalStorage, indexedDbStorage } from "@/utils/indexDBStorage";
import useVisitOtherProfileStore from "./useVisitOtherProfileStore";
import dayjs from "dayjs";
import { captureError } from "@/sentry";
import { safeJsonStringify } from "@/utils/json";

interface IProfileUpdateFormInputValues {
  id?: string;
  cognitoID?: string;
  email?: string;
  organizationGroup?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  avatarUrl?: string;
  bannerImageUrl?: string;
  bio?: string;
  jobTitle?: string;
  type?: string;
  lookingForType?: string;
  joinedAt?: string;
  phone?: string;
  points?: number;
  dateOfBirth?: string;
  interest?: string[];
  expertises?: string[];
  almaMater?: string;
  titleRank?: string;
  awards?: string[];
  gender?: string;
  race?: string;
  ethnicity?: string;
  sexuality?: string;
  vegetarian?: boolean;
  vegan?: boolean;
  personalityType?: string;
  religion?: string;
  class?: string;
  relationshipStatus?: string;
  taxBracket?: string;
  birthCountry?: string;
  birthCity?: string;
  childhoodZip?: number;
  currentZip?: number;
  dogOwner?: boolean;
  catOwner?: boolean;
  moonSign?: string;
  sunSign?: string;
  artist?: string[];
  musicalInstrument?: string[];
  morningVsNightPerson?: string;
  generation?: string;
  hasSiblings?: boolean;
  isOnboarded?: boolean;
  totalSiblings?: number;
  birthOrder?: number;
  heroes?: string[];
  sages?: string[];
  celebsLoveToMeet?: string[];
  introVsExtro?: string;
  eatOutVsEatIn?: string;
  _version?: string;
  userFocusedSkillTrackId?: string;
  userDepartmentId?: string;
  targetGoals?: { circle: number; connection: number; event: number };
  finishedUserGuideContext?: string[];
  socialMedia?: {
    linkedIn: string;
  };
  isAskedTwiceAlready?: boolean;
}

interface IUserBecomeFanMutation {
  fanUser: {
    userId: string;
  };
}

interface IUpdateProfileMutation {
  UpdateUser: IProfileUpdateFormInputValues;
}

interface IListUsersQueryVariables {
  filter: {
    _deleted: {
      ne: boolean;
    };
  };
  limit?: number;
}

const fetchUsers = async (limit?: number) => {
  const variables: IListUsersQueryVariables = {
    filter: { _deleted: { ne: true } },
    limit: limit,
  };
  const response: any = await API.graphql(
    graphqlOperation(listUsers, variables)
  );
  const records: any = response?.data?.listUsers ?? {};

  const sorted = [...records?.items].sort((a, b) => {
    const aFullName = `${a?.firstName}${a?.lastName}`;
    const bFullName = `${b?.firstName}${b?.lastName}`;
    return aFullName.localeCompare(bFullName);
  });

  return { items: sorted };
};

const fetchUser = async (id: string) => {
  const response: any = await API.graphql(graphqlOperation(getUser, { id }));
  const records: any = response?.data?.getUser ?? {};

  return records;
};

const fetchUserProfile = async (id?: string) => {
  try {
    const response: any = await API.graphql(
      graphqlOperation(getUserView, { userId: id })
    );
    const records: any = response?.data?.getUserView ?? {};
    localStorage.setItem(
      "announcements",
      records?.announcements ? records?.announcements?.length : 0
    );
    if (records?.interests) {
      return { ...records, interests: filterCategories(records.interests) };
    } else {
      return records;
    }
  } catch (error: any) {
    return error?.errors[0]?.message;
  }
};

const userBecomeFan = async (id: string, fan: boolean) => {
  const records = await API.graphql<GraphQLQuery<IUserBecomeFanMutation>>({
    query: fanUser,
    variables: { userId: id, fan },
  });

  return records;
};

const updateProfile = async (payload: IProfileUpdateFormInputValues) => {
  const records = await API.graphql<GraphQLQuery<IUpdateProfileMutation>>({
    query: updateUser,
    variables: { input: payload },
  });

  return records;
};

const useListUsers = (limit?: number) => {
  const listLimit = limit ? limit : 1000;
  return useQuery({
    queryKey: ["KEY_LIST_USERS", limit],
    queryFn: () => fetchUsers(listLimit),
  });
};

const useGetUser = (id: string) => {
  return useQuery({
    queryKey: ["KEY_GET_USER", id],
    queryFn: () => fetchUser(id),
  });
};

const useGetUserProfile = (id?: string, disabled?: boolean) => {
  const handlerLogout = useAuthStore.useLogout();
  const { setState } = useMyProfileStore();
  const { signOut } = useAuthenticator((context) => [context.signOut]);
  const router = useRouter();
  return useQuery({
    queryKey: ["KEY_GET_USER_PROFILE", id],
    queryFn: async () => {
      const res = await fetchUserProfile(id);
      if (res === "Current User Has Been Deleted") {
        handlerLogout();
        signOut();
        return router.replace({ pathname: PAGE_URL.login });
      }
      setState({ userView: res });
      return res;
    },
    refetchOnWindowFocus: true,
    enabled: !disabled,
  });
};

const useGetCGPercents = (
  userIds: string[],
  limit?: number,
  reference?: string,
  enabled?: boolean
) => {
  const { cgs } = useVisitOtherProfileStore();
  return useQuery({
    queryKey: ["KEY_GET_CG_PERCENTS", userIds, reference],
    queryFn: async () => {
      const getCgsFromZustand = cgs?.[userIds?.[0] as keyof typeof cgs];

      if (dayjs(getCgsFromZustand?.lastFetch)?.isAfter(dayjs())) {
        return {
          id: userIds[0],
          CGPercent: getCgsFromZustand?.value,
        };
      } else {
        const response: any = await API.graphql(
          graphqlOperation(getCGPercentUsers, { limit, userIds })
        );
        const records: any = response?.data?.getCGPercentUsers ?? [];

        return records;
      }
    },
    enabled: enabled ? enabled : true,
  });
};

const useUserCircleEventConnection = (
  id: string | undefined,
  isExclude: boolean = false,
  type: "USER" | "CIRCLE" | "EVENT" | "SKILL" | "SKILL_TRACK",
  enabled?: boolean
) => {
  return useQuery({
    queryKey: ["GET_DATA_USER", id, type],
    queryFn: async () => {
      const records: any = await API.graphql(
        graphqlOperation(getDataUsers, {
          isExclude,
          type,
          userId: id,
        })
      );

      return records?.data?.getDataUsers || {};
    },
    enabled: enabled ? enabled : true,
  });
};

const useUserFetchInitially = () => {
  const { login, stopLoading, logout, user } = useAuthStore();
  const { route, signOut } = useAuthenticator();
  const router = useRouter();

  const fetchAuth = useCallback(async () => {
    const token = await getFromLocalStorage("token");
    if (route === "authenticated") {
      try {
        const res = await Auth.currentAuthenticatedUser();

        const profile = res?.signInUserSession?.idToken?.payload;
        await indexedDbStorage.setItem(
          "token",
          res?.signInUserSession?.idToken?.jwtToken
        );
        login({
          profile,
          token: res?.signInUserSession?.idToken?.jwtToken,
        });
        useAuthStore.setState((prev) => ({
          ...prev,
          user: {
            profile,
            token: res?.signInUserSession?.idToken?.jwtToken,
            bio: prev.user?.bio,
          },
          isAuthenticated: true,
        }));
        return profile;
      } catch (err) {
        signOut();
        logout();
        await indexedDbStorage.removeItem("token");
        deleteDatabase("profilePicturesDB");
      } finally {
        stopLoading();
      }
    }

    if (route === "idle") {
      if (!token) {
        router.replace({ pathname: PAGE_URL.login });
        return;
      }
    }

    if (route === "signOut") {
      indexedDbStorage.removeItem("token");
      useAuthStore.setState((prev) => ({
        ...prev,
        user: {
          profile: null,
          token: "",
          bio: null,
        },
        isAuthenticated: false,
      }));
    }
  }, [route, user?.token]);

  const fetchUser = async () => {
    try {
      const res = await Auth.currentAuthenticatedUser();

      const profile = res?.signInUserSession?.idToken?.payload;
      await indexedDbStorage.setItem(
        "token",
        res?.signInUserSession?.idToken?.jwtToken
      );

      login({
        profile,
        token: res?.signInUserSession?.idToken?.jwtToken,
      });

      useAuthStore.setState((prev) => ({
        ...prev,
        user: {
          profile,
          token: res?.signInUserSession?.idToken?.jwtToken,
          bio: prev.user?.bio,
        },
        isAuthenticated: true,
      }));

      if (!res?.signInUserSession?.idToken?.payload?.user_id) {
        // User profile missing user_id. Aborting fetchUser that causes "no current user".
        return;
      }
      const resDataUser: any = await API.graphql(
        graphqlOperation(getUserLite, {
          userId: res?.signInUserSession?.idToken?.payload?.user_id,
        })
      );

      const resUserView = resDataUser?.data?.getUserView;

      useAuthStore.setState((prev) => ({
        ...prev,
        user: {
          profile: {
            ...prev.user?.profile,
          },
          bio: { ...resUserView },
          token: prev.user?.token || "",
        },
      }));

      useMyProfileStore.setState((prev) => ({
        ...prev,
        user: resUserView,
      }));
    } catch (error) {
      const errorMessage =
        (error as any)?.errors?.[0]?.message || (error as any)?.message;
        // currently let's just console.log the errors. 
        // we only want to catch errors that are unexpected / crashes
        console.warn(errorMessage);
      // if (error instanceof Error) {
      //   if (error.message.includes("No credentials")) {
      //     captureError(new Error(errorMessage), {
      //       extra: {
      //         message:
      //           "AWS credentials are missing. Prompting user to sign in.",
      //         errorResponse: safeJsonStringify(error),
      //       },
      //     });
      //   } else if (error.message.includes("No current user")) {
      //     // Do nothing, this is not an unexpected error
      //   } else {
      //     captureError(new Error(errorMessage), {
      //       extra: {
      //         message: "Unexpected Error",
      //         errorResponse: safeJsonStringify(error),
      //       },
      //     });
      //   }
      // } else {
      //   captureError(new Error(errorMessage), {
      //     extra: {
      //       message: "GraphQL request failed",
      //       errorResponse: safeJsonStringify(error),
      //     },
      //   });
      // }
    } finally {
      stopLoading();
    }
  };

  return {
    fetchUser,
  };
};

export {
  useListUsers,
  useGetUser,
  userBecomeFan,
  updateProfile,
  useGetUserProfile,
  fetchUserProfile,
  useGetCGPercents,
  useUserCircleEventConnection,
  useUserFetchInitially,
};
